import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    Avatar,
    Paper,
} from '@mui/material';
import FlightIcon from '@mui/icons-material/Flight';
import HotelIcon from '@mui/icons-material/Hotel';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AttractionsIcon from '@mui/icons-material/Attractions';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { parseLocalDate, toLocalDateString } from '../../utils/dateHelpers';

interface TripItem {
    id: string;
    item_type?: string;
    itemType?: string;
    title: string;
    location?: string;
    start_date?: string;
    startDate?: string;
    end_date?: string;
    endDate?: string;
    start_time?: string;
    startTime?: string;
    estimated_cost?: number;
    estimatedCost?: number;
    notes?: string;
    _spanMarker?: 'start' | 'middle' | 'end';
}

interface ItineraryTimelineProps {
    items: TripItem[];
    startDate?: string;
    endDate?: string;
    destination: string;
}

const ItineraryTimeline: React.FC<ItineraryTimelineProps> = ({
    items,
    startDate,
    endDate,
    destination,
}) => {
    // Group items by date
    const groupItemsByDate = () => {
        if (!startDate || !endDate) return {};
        
        const start = parseLocalDate(startDate)!;
        const end = parseLocalDate(endDate)!;
        const groups: Record<string, TripItem[]> = {};
        
        // Create empty groups for each day
        const current = new Date(start);
        while (current <= end) {
            const dateKey = toLocalDateString(current);
            groups[dateKey] = [];
            current.setDate(current.getDate() + 1);
        }
        
        // Add items to their respective days
        items.forEach(item => {
            const itemDate = item.start_date || item.startDate;
            const itemEndDate = item.end_date || item.endDate;
            const itemType = item.item_type || item.itemType;

            // Handle items with no date: add to first day
            if (!itemDate) {
                const firstKey = Object.keys(groups)[0];
                if (firstKey) groups[firstKey].push(item);
                return;
            }

            const startKey = itemDate.split('T')[0];

            // Flights: keep original single-placement + return-flight special handling
            if (itemType === 'flight') {
                // Place outbound on start day
                if (groups[startKey]) {
                    groups[startKey].push(item);
                } else {
                    const firstKey = Object.keys(groups)[0];
                    if (firstKey) groups[firstKey].push(item);
                }

                // If round-trip, add synthetic return-flight card on end day
                const notes = item.notes || '';
                const title = item.title || '';
                if (itemEndDate && itemEndDate !== itemDate) {
                    const endDateKey = itemEndDate.split('T')[0];
                    if (groups[endDateKey]) {
                        let returnTitle = 'Return Flight';
                        let returnNotes = '';

                        if (notes.includes('Return:')) {
                            const returnMatch = notes.match(/Return:\s*([^|]+)/);
                            if (returnMatch) {
                                returnNotes = returnMatch[1].trim();
                                const flightNumMatch = returnNotes.match(/^([A-Z]{2}\s*\d+)/);
                                if (flightNumMatch) {
                                    returnTitle = `Return: ${title.split(' - ')[0]} ${flightNumMatch[1]}`.trim();
                                } else {
                                    returnTitle = `Return: ${title.split(' - ')[0]}`.replace('(Round Trip)', '').trim();
                                }
                            }
                        } else if (title.includes('Round Trip') || title.includes('↔')) {
                            const airlineName = title.split(' - ')[0] || title.split(' ↔ ')[0] || 'Flight';
                            returnTitle = `Return: ${airlineName}`.trim();
                            returnNotes = notes.includes('|') ? notes.split('|').find(s => s.toLowerCase().includes('return'))?.trim() || '' : '';
                        }

                        const returnItem: TripItem = {
                            ...item,
                            id: `${item.id}-return`,
                            title: returnTitle,
                            notes: returnNotes || 'Return flight home',
                        };
                        groups[endDateKey].push(returnItem);
                    }
                }
                return;
            }

            // Non-flight single-day items: place on start day only
            if (!itemEndDate || itemEndDate === itemDate) {
                if (groups[startKey]) {
                    groups[startKey].push(item);
                } else {
                    const firstKey = Object.keys(groups)[0];
                    if (firstKey) groups[firstKey].push(item);
                }
                return;
            }

            // Non-flight multi-day items (hotels, cars, multi-day activities):
            // Place a tagged copy on every day the item spans.
            const endKey = itemEndDate.split('T')[0];
            Object.keys(groups).forEach(dayKey => {
                if (dayKey >= startKey && dayKey <= endKey) {
                    const marker = dayKey === startKey ? 'start' : dayKey === endKey ? 'end' : 'middle';
                    groups[dayKey].push({ ...item, _spanMarker: marker });
                }
            });
        });
        
        return groups;
    };
    
    const getItemIcon = (type?: string) => {
        switch (type) {
            case 'flight': return <FlightIcon sx={{ fontSize: 18 }} />;
            case 'hotel': return <HotelIcon sx={{ fontSize: 18 }} />;
            case 'car': return <DirectionsCarIcon sx={{ fontSize: 18 }} />;
            case 'transport': return <DirectionsCarIcon sx={{ fontSize: 18 }} />;
            case 'activity': return <AttractionsIcon sx={{ fontSize: 18 }} />;
            default: return <AttractionsIcon sx={{ fontSize: 18 }} />;
        }
    };
    
    const getItemColor = (type?: string) => {
        switch (type) {
            case 'flight': return '#2196F3';
            case 'hotel': return '#4A6FA5';
            case 'car': return '#607D8B';
            case 'transport': return '#607D8B';
            case 'activity': return '#2D6E6A';
            default: return '#2D6E6A';
        }
    };
    
    const formatDate = (dateStr: string, index: number) => {
        const date = parseLocalDate(dateStr)!;
        return {
            dayNum: index + 1,
            weekday: date.toLocaleDateString('en-US', { weekday: 'short' }),
            month: date.toLocaleDateString('en-US', { month: 'short' }),
            day: date.getDate(),
        };
    };
    
    const groupedItems = groupItemsByDate();
    const dateKeys = Object.keys(groupedItems);
    
    if (dateKeys.length === 0 || items.length === 0) {
        return (
            <Card sx={{ borderRadius: 3, mb: 3 }}>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <CalendarTodayIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                    <Typography color="text.secondary">
                        Add trip dates and items to see your itinerary
                    </Typography>
                </CardContent>
            </Card>
        );
    }
    
    return (
        <Card sx={{ borderRadius: 3, mb: 3, overflow: 'hidden' }}>
            <Box sx={{ 
                bgcolor: '#2D6E6A', 
                color: 'white', 
                px: 2, 
                py: 1.5, 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1 
            }}>
                <CalendarTodayIcon />
                <Typography variant="h6">Day-by-Day Itinerary</Typography>
                <Chip 
                    label={`${dateKeys.length} days`} 
                    size="small" 
                    sx={{ ml: 'auto', bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
                />
            </Box>
            
            <CardContent sx={{ p: 0 }}>
                {dateKeys.map((dateKey, dayIndex) => {
                    const dayItems = groupedItems[dateKey];
                    const dateInfo = formatDate(dateKey, dayIndex);
                    const isFirstDay = dayIndex === 0;
                    const isLastDay = dayIndex === dateKeys.length - 1;
                    
                    return (
                        <Box key={dateKey} sx={{ display: 'flex', borderBottom: dayIndex < dateKeys.length - 1 ? '1px solid #eee' : 'none' }}>
                            {/* Day indicator */}
                            <Box sx={{ 
                                width: 80, 
                                p: 2, 
                                bgcolor: isFirstDay ? '#E8F5F3' : isLastDay ? '#FFF3E0' : '#F5F5F5',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                borderRight: '1px solid #eee',
                            }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#666' }}>
                                    Day {dateInfo.dayNum}
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: isFirstDay ? '#2D6E6A' : isLastDay ? '#FF6B35' : '#333' }}>
                                    {dateInfo.day}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {dateInfo.weekday}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {dateInfo.month}
                                </Typography>
                            </Box>
                            
                            {/* Day content */}
                            <Box sx={{ flex: 1, p: 2 }}>
                                {dayItems.length > 0 ? (
                                    dayItems.map((item, idx) => {
                                        const type = item.item_type || item.itemType;
                                        const time = item.start_time || item.startTime;
                                        const cost = Number(item.estimated_cost) || Number(item.estimatedCost) || 0;
                                        const spanMarker = item._spanMarker;

                                        // Middle-day marker: render a compact "Staying at..." line instead of full card
                                        if (spanMarker === 'middle') {
                                            const middleLabel =
                                                type === 'hotel' ? `Staying at ${item.title}` :
                                                type === 'car' ? `Car: ${item.title}` :
                                                type === 'activity' ? `${item.title} (continues)` :
                                                `${item.title} (continues)`;
                                            return (
                                                <Box
                                                    key={item.id || idx}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        py: 0.75,
                                                        px: 1.5,
                                                        mb: idx < dayItems.length - 1 ? 0.75 : 0,
                                                        borderRadius: 2,
                                                        bgcolor: `${getItemColor(type)}10`,
                                                        borderLeft: `3px solid ${getItemColor(type)}`,
                                                    }}
                                                >
                                                    <Box sx={{ color: getItemColor(type), display: 'flex', alignItems: 'center' }}>
                                                        {getItemIcon(type)}
                                                    </Box>
                                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.85rem' }}>
                                                        {middleLabel}
                                                    </Typography>
                                                </Box>
                                            );
                                        }

                                        // Start/end days: prefix the title to clarify boundary event
                                        let titleDisplay = item.title;
                                        if (spanMarker === 'start') {
                                            const prefix =
                                                type === 'hotel' ? 'Check-in: ' :
                                                type === 'car' ? 'Pickup: ' :
                                                type === 'activity' ? 'Begins: ' : '';
                                            titleDisplay = `${prefix}${item.title}`;
                                        } else if (spanMarker === 'end') {
                                            const prefix =
                                                type === 'hotel' ? 'Check-out: ' :
                                                type === 'car' ? 'Return: ' :
                                                type === 'activity' ? 'Ends: ' : '';
                                            titleDisplay = `${prefix}${item.title}`;
                                        }

                                        return (
                                            <Paper 
                                                key={item.id || idx}
                                                elevation={0}
                                                sx={{ 
                                                    p: 1.5, 
                                                    mb: idx < dayItems.length - 1 ? 1.5 : 0,
                                                    borderRadius: 2,
                                                    border: `1px solid ${getItemColor(type)}30`,
                                                    bgcolor: `${getItemColor(type)}08`,
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                                    <Avatar sx={{ 
                                                        width: 32, 
                                                        height: 32, 
                                                        bgcolor: `${getItemColor(type)}20`,
                                                        color: getItemColor(type),
                                                    }}>
                                                        {getItemIcon(type)}
                                                    </Avatar>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                                                            {titleDisplay}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
                                                            {time && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <AccessTimeIcon sx={{ fontSize: 14 }} />
                                                                    {time}
                                                                </Typography>
                                                            )}
                                                            {item.location && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <LocationOnIcon sx={{ fontSize: 14 }} />
                                                                    {item.location}
                                                                </Typography>
                                                            )}
                                                            {cost > 0 && (
                                                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#FF6B35' }}>
                                                                    ${cost.toLocaleString()}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        {item.notes && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                                                                {item.notes}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Paper>
                                        );
                                    })
                                ) : (
                                    <Box sx={{ py: 2, textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                            {isFirstDay ? '🛬 Arrival day' : isLastDay ? '🛫 Departure day' : '🌴 Free day - explore!'}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </CardContent>
        </Card>
    );
};

export default ItineraryTimeline;
